<template>
  <nav v-if="groups.length" class="glt-nav" role="navigation">
    <template v-for="group in groups">
      <el-dropdown
        v-if="navLinks(group).length > 1"
        :key="group.key"
        trigger="hover"
        placement="bottom-start"
        :show-timeout="80"
        :hide-timeout="120"
      >
        <button
          type="button"
          class="glt-nav__tab"
          :class="{ 'is-active': isGroupActive(group.key) }"
        >
          <i :class="groupIcon(group.key)"></i>
          <span>{{ $t('navGroups.' + group.key) }}</span>
          <i class="el-icon-arrow-down glt-nav__caret"></i>
        </button>
        <el-dropdown-menu slot="dropdown" class="glt-nav-menu" :class="{ 'is-wide': navLinks(group).length > 4 }">
          <router-link
            v-for="item in navLinks(group)"
            :key="item.id"
            :to="item.routePath"
            class="glt-nav-menu__item"
            :class="{ 'is-active': isRouteActive(item.routePath) }"
            @click.native="$emit('navigate')"
          >
            <i :class="item.icon"></i>
            <span>{{ item.label }}</span>
            <em v-if="badgeValue(item)" class="glt-nav-menu__badge">{{ badgeValue(item) }}</em>
          </router-link>
        </el-dropdown-menu>
      </el-dropdown>

      <router-link
        v-else-if="navLinks(group).length === 1"
        :key="group.key + '-link'"
        :to="navLinks(group)[0].routePath"
        class="glt-nav__tab"
        :class="{ 'is-active': isRouteActive(navLinks(group)[0].routePath) }"
        @click.native="$emit('navigate')"
      >
        <i :class="navLinks(group)[0].icon || groupIcon(group.key)"></i>
        <span>{{ navLinks(group)[0].label }}</span>
      </router-link>
    </template>
  </nav>
</template>

<script>
import { groupIcon, routeGroup } from '@/config/navigation'

export default {
  name: 'GltTopNav',
  props: {
    groups: {
      type: Array,
      default: () => []
    },
    badgeValue: {
      type: Function,
      default: () => 0
    },
    isRouteActive: {
      type: Function,
      required: true
    }
  },
  methods: {
    groupIcon,
    isGroupActive(key) {
      return routeGroup(this.$route.path) === key
    },
    navLinks(group) {
      const out = []
      ;(group.items || []).forEach(item => {
        if (item.children && item.children.length) {
          item.children.forEach(child => out.push(child))
        } else {
          out.push(item)
        }
      })
      return out
    }
  }
}
</script>

<style scoped>
.glt-nav {
  display: flex;
  align-items: stretch;
  gap: 2px;
  min-width: 0;
  flex: 1;
  padding: 0 8px;
  overflow-x: auto;
  scrollbar-width: none;
}

.glt-nav::-webkit-scrollbar {
  display: none;
}

.glt-nav__tab {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 36px;
  padding: 0 12px;
  border: 0;
  border-radius: 8px;
  background: transparent;
  color: var(--color-text-sub);
  text-decoration: none;
  font-size: 13px;
  font-weight: 400;
  font-family: inherit;
  white-space: nowrap;
  cursor: pointer;
  position: relative;
  transition: color 0.15s ease, background 0.15s ease;
}

.glt-nav__tab i:first-child {
  font-size: 15px;
}

.glt-nav__caret {
  font-size: 10px;
  opacity: 0.5;
  margin-left: -2px;
}

.glt-nav__tab:hover {
  color: var(--color-text);
  background: var(--layout-hover-bg);
}

.glt-nav__tab.is-active {
  color: var(--color-brand);
  font-weight: 500;
}

.glt-nav__tab.is-active::after {
  content: '';
  position: absolute;
  left: 10px;
  right: 10px;
  bottom: 0;
  height: 2px;
  border-radius: 2px 2px 0 0;
  background: var(--color-brand);
}

.glt-nav-menu__item {
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 168px;
  padding: 9px 14px;
  color: var(--color-text-sub);
  text-decoration: none;
  font-size: 13px;
  border-radius: 6px;
  transition: background 0.12s ease, color 0.12s ease;
}

.glt-nav-menu__item i {
  width: 16px;
  text-align: center;
  font-size: 14px;
}

.glt-nav-menu__item span {
  flex: 1;
}

.glt-nav-menu__item:hover {
  background: var(--layout-hover-bg);
  color: var(--color-text);
}

.glt-nav-menu__item.is-active {
  background: var(--color-brand-soft);
  color: var(--color-brand);
  font-weight: 500;
}

.glt-nav-menu__badge {
  min-width: 18px;
  padding: 0 6px;
  border-radius: 999px;
  background: var(--color-surface-muted);
  color: var(--color-text-sub);
  font-size: 11px;
  font-style: normal;
  line-height: 18px;
  text-align: center;
}

.glt-nav-menu__item.is-active .glt-nav-menu__badge {
  background: rgba(37, 99, 235, 0.15);
  color: var(--color-brand);
}
</style>

<style>
.glt-nav-menu.el-dropdown-menu {
  padding: 6px !important;
  border: 1px solid var(--color-border) !important;
  border-radius: 10px !important;
  box-shadow: var(--shadow-float) !important;
  margin-top: 4px !important;
}

.glt-nav-menu.el-dropdown-menu .popper__arrow {
  display: none;
}

.glt-nav-menu.el-dropdown-menu.is-wide {
  display: grid !important;
  grid-template-columns: 1fr 1fr;
  gap: 2px;
  min-width: 340px;
}
</style>
